import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import AttachDownload from '../../assets/attach_download.svg';
import BtArrowDown from '../../assets/bt_arrow_down.svg';
import BtArrowUp from '../../assets/bt_arrow_up.svg';
import LeftArrowMobile from '../../assets/left_arrow_mobile.svg';
import RightArrowMobile from '../../assets/right_arrow_mobile.svg';
import { CenterLayout, HorizontalLayout, useResponsivePadding, VerticalLayout } from '../../common/common-layout';
import { Body2, Body3, Date2, H3, H9, Point2, Point3, TitleNormal } from '../../common/common-text';
import BTBox from '../../common/components/BTBox';
import BTButton from '../../common/components/BTButton';
import BTImageInput from '../../common/components/BTImageInput';
import { BTOutlineButton } from '../../common/components/BTOutlineButton';
import { MediaPost } from '../../common/data/MediaData';
import { fetchMarketings, fetchTabManagers } from '../../common/firebase/FirestoreService';
import { pxToRem } from '../../common/utils/ConvertUtils';
import '../../css/bbpage.css';
import { useDeviceType } from '../../hooks/useDeviceType';
import 'react-quill/dist/quill.snow.css';

type CompanyNewsSection1rops = {
  section1Height: number;
};

const CompanyNewsSection1 = ({ section1Height }: CompanyNewsSection1rops) => {
  const INITIAL_VISIBLE_COUNT = 12;

  const { t } = useTranslation();
  const { horizontal: padding } = useResponsivePadding();
  const [searchText, setSearchText] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<MediaPost[]>([]);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [selectedPost, setSelectedPost] = useState<MediaPost | null>(null);
  const [scrollPosY, setScrollPosY] = useState(0);
  const { isMobile } = useDeviceType();
  const [mediaPosts, setMediaPosts] = useState<MediaPost[]>([]);
  const [detailText, setDetailText] = useState('');

  // 뒤로가기 처리
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (selectedPost) {
        // 히스토리 back 막고 custom 동작 실행
        event.preventDefault(); // 일부 브라우저에서 무시될 수 있음
        setSelectedPost(null);
        setDetailText('');
        window.scrollTo({ top: scrollPosY, behavior: 'instant' });

        // 히스토리 "앞으로 가기" 방지
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // clean up
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedPost, scrollPosY]);

  const getCategoriesFromPosts = (posts: MediaPost[]) => {
    const uniqueTypes = Array.from(new Set(posts.map(post => post.type))).filter(Boolean);
    return [
      { label: '전체', active: true },
      ...uniqueTypes.map(type => ({ label: type, active: false }))
    ];
  };

  const [jobCategories, setJobCategories] = useState(getCategoriesFromPosts(mediaPosts));

  const handleCategoryClick = (index: number) => {
    const newCategories = jobCategories.map((item, i) => ({
      ...item,
      active: i === index,
    }));
    setJobCategories(newCategories);

    const selectedLabel = newCategories[index].label;

    const filtered = mediaPosts.filter((post: MediaPost) => {
      const inCategory = selectedLabel === '전체' || post.type === selectedLabel;
      const matchesSearch = (post.title + ' ' + post.content + ' ' + post.date)
        .toLowerCase()
        .includes(searchText.trim().toLowerCase());
      return inCategory && matchesSearch;
    });

    setFilteredPosts(filtered);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const handleSearch = () => {
    const filtered = mediaPosts.filter((post: MediaPost) => {
      const searchableString = [
        post.title,
        post.content,
        post.date,
      ].join(' ').toLowerCase();

      return searchableString.includes(searchText.trim().toLowerCase());
    });

    setFilteredPosts(filtered);
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  };

  const handleShowMore = () => {
    setVisibleCount(prev => prev + INITIAL_VISIBLE_COUNT);
  };

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMore = visibleCount < filteredPosts.length;

  const handleSave = () => {
    setSelectedPost(null);
    setDetailText('');
  };

  const handleToList = () => {
    setSelectedPost(null);
    setDetailText('');
    setTimeout(() => {
      window.scrollTo({ top: scrollPosY, behavior: 'instant' });
    }, 50);
  };

  const detailContent = () => {
    return (
      <div
        className="detail-page"
        style={{
          color: 'black',
          maxWidth: '100%',
          overflowX: 'auto',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
        }}
        dangerouslySetInnerHTML={{
          __html: `<div class="ql-snow"><div class="ql-editor">${detailText}</div></div>`,
        }}
      />
    );
  };

  const createItem = (post: MediaPost) => (
    <VerticalLayout
      backgroundColor='#FAFAFA'
      hoverBackgroundColor="#EEEEF1"
      style={{ cursor: 'pointer' }}
      onClick={() => {
        setSelectedPost(post);
        setDetailText(post.content);
        setScrollPosY(window.scrollY);
        window.history.pushState(null, '', window.location.href);
        window.scrollTo({ top: section1Height, behavior: 'instant' });
      }}
      data-analytics-name={`사내소식_${post.title}`}
    >
      <div style={{ width: '100%', aspectRatio: '1.25', backgroundColor: '#f0f0f0' }}>
        <img src={post.imageUrl} style={{ width: '100%', aspectRatio: '1.25', objectFit: 'cover', objectPosition: 'top' }} />
      </div>
      <BTBox height={pxToRem(30)} />
      <Body3 style={{ paddingLeft: pxToRem(28), height: pxToRem(80), display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>{post.title}</Body3>
      <BTBox height={pxToRem(20)} />
      <Point3 style={{ paddingLeft: pxToRem(28), color: '#979797' }}>{post.date}</Point3>
      <BTBox height={pxToRem(24)} />
    </VerticalLayout>
  );

  useEffect(() => {
    const fetchData = async () => {
      const tab = await fetchTabManagers('marketing');
      const tabCodes = tab.map(item => item.tabCode);
      const marketings = await fetchMarketings(tabCodes);
      const allData: MediaPost[] = marketings
        .filter(item => item.marketingType === 'companyInfo')
        .map((item, index) => ({
          index: index,
          imageUrl: item.representImage ?? '',
          title: item.title,
          content: item.contents,
          date: item.postDate
            ? item.postDate.toISOString().slice(0, 10)
            : '', // 날짜가 null이면 빈 문자열
          type: tab.find((t) => t.tabCode === item.marketingType)?.tabName ?? '', // tab에서 이름 매핑
          attachedFiles: item.attachedFiles,
        }));

      setMediaPosts(allData);
    };

    fetchData();
  }, []);

  useEffect(() => {
    setFilteredPosts(mediaPosts);
    setVisibleCount(Math.min(INITIAL_VISIBLE_COUNT, mediaPosts.length));
    setJobCategories(getCategoriesFromPosts(mediaPosts));
  }, [mediaPosts]);

  return (
    <VerticalLayout paddingLeft={padding} paddingRight={padding} style={{ background: 'white' }}>
      <BTBox height={isMobile ? 40 : `${pxToRem(100)}`} />
      <H3 style={{ marginBottom: isMobile ? 21 : pxToRem(-40) }}><Trans i18nKey='사내소식' /></H3>

      {!isMobile ? (
        <>
          <HorizontalLayout style={{ alignItems: 'center' }}>
            {/* {jobCategories.map((item, index) => (
                  <React.Fragment key={item.label}>
                    <Point2
                      style={{ color: item.active ? 'black' : '#979797', cursor: 'pointer' }}
                      onClick={() => handleCategoryClick(index)}
                    >
                      {item.label}
                    </Point2>
                    <BTBox width={30} />
                    {(index < jobCategories.length - 1) && <VerticalLayout height={20} width={1} backgroundColor='black' style={{ opacity: 0.2 }} />}
                    <BTBox width={30} />
                  </React.Fragment>
                ))} */}
            <VerticalLayout style={{ flex: 1 }} />
            <div style={{ width: '480px', visibility: selectedPost ? 'hidden' : 'visible' }}>
              <BTImageInput
                hint='Search'
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </div>
          </HorizontalLayout>
          <BTBox height={36} />
          <VerticalLayout height={2} backgroundColor='black' />
        </>
      ) : (
        <>
          {!selectedPost ? (
            <div style={{
              width: "100%",
              height: "40px",
              border: "1px solid #ccc",     // ✅ 테두리 추가
              borderRadius: "6px",          // ✅ 둥근 모서리 적용
              overflow: "hidden",           // ✅ 내부 인풋과 테두리 깔끔하게 맞추기
              display: "flex",
              alignItems: "center",
              backgroundColor: "white",     // ✅ 드롭다운 느낌
              boxSizing: "border-box",
            }}>
              <BTImageInput
                hint='Search'
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </div>
          ) : (
            <div style={{ width: '100%', height: '1px', backgroundColor: 'black' }}></div>
          )}
        </>
      )}

      {!selectedPost && (
        <>
          <BTBox height={pxToRem(79)} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)', // 가로 3칸
              rowGap: '40px',
              columnGap: '40px',
              width: '100%',
            }}
          >
            {visiblePosts.map(createItem)}
          </div>
        </>

      )}

      {(!selectedPost && hasMore) && (
        <>
          <BTBox height={pxToRem(150)} />
          <CenterLayout>
            <div style={{ width: isMobile ? '100%' : '335px' }}>
              <BTOutlineButton
                color="#464646"
                borderColor="#979797"
                centered={true}
                onClick={handleShowMore}
                borderWidth={1}
                height={isMobile ? 60 : pxToRem(102)}
                fontSize={isMobile ? 15 : pxToRem(28)}
                data-analytics-name="사내소식_더보기"
              >
                {t('더보기')}
              </BTOutlineButton>
            </div>
          </CenterLayout>
        </>
      )}

      {/* 상세 화면 */}
      {selectedPost && (
        <>
          <HorizontalLayout
            paddingTop={isMobile ? 16 : `${pxToRem(40)}`}
            paddingBottom={isMobile ? 16 : `${pxToRem(40)}`}
            style={{
              alignItems: isMobile ? 'flex-start' : 'center',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? pxToRem(10) : undefined,
            }}
          >
            <Body2 style={{
              fontSize: isMobile ? '18px' : `${pxToRem(38)}`,
              ...isMobile ? { lineHeight: '24px' } : {}
            }}>{selectedPost.title}</Body2>
            <VerticalLayout style={{ flex: 1 }} />
            <Date2 style={{ color: '#464646' }}>{selectedPost.date}</Date2>
          </HorizontalLayout>
          <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />
          {
            selectedPost.attachedFiles.length > 0 && (
              <>
                <BTBox height={pxToRem(31)} />
                <VerticalLayout backgroundColor='#F4F4F4'>
                  <BTBox height={pxToRem(15)} />
                  {selectedPost.attachedFiles.map((fileUrl, index) => {
                    const decodedUrl = decodeURIComponent(fileUrl);
                    const cleanUrl = decodedUrl.split('?')[0];
                    const fileName = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
                    const forcedUrl = `${fileUrl}&response-content-disposition=attachment%3B%20filename%3D${encodeURIComponent(fileName)}`;

                    return (
                      <HorizontalLayout
                        key={index}
                        style={{
                          marginBottom: pxToRem(15),
                          marginTop: pxToRem(15),
                          color: '#F4F4F4',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <BTBox width={pxToRem(54)} />
                        <TitleNormal fontSize={24}>
                          {fileName}
                        </TitleNormal>
                        <VerticalLayout style={{ flex: 1 }} />
                        <a
                          href={forcedUrl}
                          download
                        >
                          <img
                            src={AttachDownload}
                            style={{ width: '40px', height: '40px', cursor: 'pointer' }}
                            alt="Download"
                          />
                        </a>
                        <BTBox width={pxToRem(54)} />
                      </HorizontalLayout>
                    );
                  })}
                  <BTBox height={pxToRem(15)} />
                </VerticalLayout>
              </>
            )
          }
          <BTBox height={pxToRem(40)} />

          {/* 상세 내용 */}
          {detailContent()}

          <BTBox height={pxToRem(50)} />
          {/* selectedPost 앞에 포스트 있는지 확인 */}
          {isMobile ? (
            <>
              <div style={{ width: '100%', height: '1px', backgroundColor: 'black', opacity: 0.2 }} />
              <HorizontalLayout style={{ width: '100%' }}>
                {/* selectedPost 앞에 포스트 있는지 확인 */}
                {(() => {
                  const prevPost = [...filteredPosts]
                    .filter((post) => post.index < selectedPost.index)
                    .sort((a, b) => b.index - a.index)[0]; // 가장 큰 index 찾기

                  return prevPost ? (
                    <VerticalLayout>
                      <HorizontalLayout style={{ alignItems: 'center', cursor: 'pointer', paddingTop: '20px', paddingBottom: '20px' }} onClick={() => {
                        setSelectedPost(prevPost)
                        setDetailText(prevPost.content);
                        window.history.pushState(null, '', window.location.href);
                        window.scrollTo({ top: section1Height, behavior: 'instant' });
                      }} data-analytics-name="미디어센터_이전글">
                        <img src={LeftArrowMobile} />
                        <BTBox width={14} />
                        <Point2>{t('이전 글')}</Point2>
                      </HorizontalLayout>
                    </VerticalLayout>
                  ) : null;
                })()}

                {/* selectedPost 뒤에 포스트 있는지 확인 */}
                {(() => {
                  const prevPost = [...filteredPosts]
                    .filter((post) => post.index > selectedPost.index)
                    .sort((a, b) => a.index - b.index)[0];

                  return prevPost ? (
                    <VerticalLayout style={{ alignItems: 'end' }}>
                      <HorizontalLayout wrapContents={true} style={{ alignItems: 'center', cursor: 'pointer', paddingTop: '20px', paddingBottom: '20px' }} onClick={() => {
                        setSelectedPost(prevPost)
                        setDetailText(prevPost.content);
                        window.history.pushState(null, '', window.location.href);
                        window.scrollTo({ top: section1Height, behavior: 'instant' });
                      }} data-analytics-name="미디어센터_다음글">
                        <Point2>{t('다음 글')}</Point2>
                        <BTBox width={14} />
                        <img src={RightArrowMobile} />
                      </HorizontalLayout>
                    </VerticalLayout>
                  ) : null;
                })()}
              </HorizontalLayout>
              <div style={{ width: '100%', height: '1px', backgroundColor: 'black', opacity: 0.2 }} />
            </>
          ) : (
            <>
              {(() => {
                const prevPost = [...filteredPosts]
                  .filter((post) => post.index < selectedPost.index)
                  .sort((a, b) => b.index - a.index)[0]; // 가장 큰 index 찾기

                return prevPost ? (
                  <VerticalLayout>
                    <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />
                    <BTBox height={pxToRem(60)} />
                    <HorizontalLayout style={{ alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      setSelectedPost(prevPost)
                      setDetailText(prevPost.content);
                      window.history.pushState(null, '', window.location.href);
                      window.scrollTo({ top: section1Height, behavior: 'instant' });
                    }} data-analytics-name="사내소식_이전글">
                      <img src={BtArrowUp} style={{width: pxToRem(30), height: pxToRem(30)}} />
                      <BTBox width={13} />
                      <Point2>{t('이전 글')}</Point2>
                      <BTBox width={30} />
                      <H9 style={{ marginRight: 'auto' }}>{prevPost.title}</H9>
                      <Date2 style={{ color: '#464646' }}>{prevPost.date}</Date2>
                    </HorizontalLayout>
                    <BTBox height={pxToRem(40)} />
                  </VerticalLayout>
                ) : null;
              })()}

              <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />

              {/* selectedPost 뒤에 포스트 있는지 확인 */}
              {(() => {
                const prevPost = [...filteredPosts]
                  .filter((post) => post.index > selectedPost.index)
                  .sort((a, b) => a.index - b.index)[0];

                return prevPost ? (
                  <VerticalLayout>
                    <BTBox height={pxToRem(40)} />
                    <HorizontalLayout style={{ alignItems: 'center', cursor: 'pointer' }} onClick={() => {
                      setSelectedPost(prevPost)
                      setDetailText(prevPost.content);
                      window.history.pushState(null, '', window.location.href);
                      window.scrollTo({ top: section1Height, behavior: 'instant' });
                    }} data-analytics-name="사내소식_다음글">
                      <img src={BtArrowDown} style={{width: pxToRem(30), height: pxToRem(30)}}/>
                      <BTBox width={13} />
                      <Point2>{t('다음 글')}</Point2>
                      <BTBox width={30} />
                      <H9 style={{ marginRight: 'auto' }}>{prevPost.title}</H9>
                      <Date2 style={{ color: '#464646' }}>{prevPost.date}</Date2>
                    </HorizontalLayout>
                    <BTBox height={pxToRem(40)} />
                    <VerticalLayout height={1} backgroundColor="black" style={{ opacity: '0.2' }} />
                  </VerticalLayout>
                ) : null;
              })()}
            </>
          )}

          <BTBox height={pxToRem(80)} />
          <CenterLayout>
            <HorizontalLayout wrapContents={isMobile ? false : true}>
              <BTButton width={isMobile ? '100%' : `${pxToRem(359)}`} height={isMobile ? '60px' : `${pxToRem(102)}`}
                color='#E1E1E1'
                textColor='#707070'
                borderColor='#C2C2C2'
                hoverBorderColor='#979797'
                hoverColor='#F5F5F5'
                hoverTextColor='#464646'
                sx={{
                  fontWeight: 700,
                  fontSize: isMobile ? 15 : `${pxToRem(28)}`,
                }}
                onClick={handleToList} data-analytics-name="사내소식_목록보기">{t('목록보기')}</BTButton>
            </HorizontalLayout>
          </CenterLayout>
        </>
      )}

      <BTBox height={200} />
    </VerticalLayout>
  );
};

export default CompanyNewsSection1;
